import { useCallback, useState } from "react";
import { collectionApi } from "src/common/api/collection/collection-rtk-api";
import { CryptoAPI } from "src/common/api/crypto/crypto-api";
import { useAddOrganizationMembersMutation, useCreateOrganizationMutation, useOrgSubsumeMemberMutation, useRespondToAdminApprovalMutation } from "src/common/api/crypto/crypto-rtk-api";

interface UseAddOrgUserResult {
  addNewUsers: (admin_id: string, org_id: string, user_id: string, display_name: string, role: string, department: string) => Promise<void>;
  createNewOrg: (user_id: string, org_name: string) => Promise<string>;
  addExistingMember: (admin_id: string, org_id: string, user_id: string, department: string) => Promise<void>;
  success: boolean;
  setSuccess: (success: boolean) => void;
}

export function useAddOrgUser(): UseAddOrgUserResult {
  const [addOrgMembers] = useAddOrganizationMembersMutation();
  const [createOrg] = useCreateOrganizationMutation();
  const [addOrganizationMembers] = useOrgSubsumeMemberMutation();
  const [respondToAdminApproval] = useRespondToAdminApprovalMutation();
  const [success, setSuccess] = useState(false);
  const [trigger] = collectionApi.endpoints.getTestUsersSecret.useLazyQuery();

  // Description: Add a new user that have NOT been created to an organization, the user will be added as an standard member
  const addNewUsers = useCallback(async (admin_id: string, org_id: string, user_id: string, display_name: string, role: string, department: string) => {
    const users = [{user_id, display_name, role, department}];
    await addOrgMembers({userId: admin_id, orgId: org_id, users});
    const { secret, metadata } = await trigger({ userId: user_id, secretType: "account_setup" }).unwrap();
    const data = {
      user_id,
      secret,
      key_version: Number(metadata.key_version),
    };
    await CryptoAPI.setupNewAccount(data);
  }, [addOrgMembers]);

  // Description: Create a new organization, the user will be added as an admin
  const createNewOrg = useCallback(async (user_id: string, org_name: string) => {
    const response = await createOrg({userId: user_id, orgName: org_name, userDepartment: "Dept"}).unwrap();
    return response.org_id;
  }, [createOrg]);

  // Description: Add an existing member to an organization, the user will be added as a standard member
  const addExistingMember = useCallback(async (admin_id: string, org_id: string, user_id: string, department: string) => {
    const response = await addOrganizationMembers({
      userId: admin_id, 
      orgId: org_id, 
      subsumeUserId: user_id,
      department
    }).unwrap();
    await respondToAdminApproval({
      userId: user_id,
      orgId: org_id,
      requestId: response.request?.request_id || "",
      response: true,
      request: response.request?.payload || ""
    });
  }, [addOrganizationMembers, respondToAdminApproval]);

  return { 
    addNewUsers, 
    createNewOrg, 
    addExistingMember, 
    success, 
    setSuccess 
  };
}
